import { MARKING_REMOVED_TAGS, MARKING_STORED_TAGS } from '#shared/utils/markings/rules'

export type HtmlSanitizationPolicy = 'highlight' | 'richText' | 'jats' | 'rss' | 'marking'

export interface HtmlSanitizationConfig {
  ALLOWED_TAGS: string[]
  ALLOWED_ATTR: string[]
  ALLOWED_STYLES?: Record<string, Record<string, RegExp[]>>
  /** Restricts URL attributes; DOMPurify's own default admits `data:` URIs. */
  ALLOWED_URI_REGEXP?: RegExp
  /**
   * Tags dropped with their content. Deliberately not DOMPurify's
   * `FORBID_CONTENTS` option: that one *replaces* the library's default set, so
   * a tag added upstream after a new mXSS vector would silently stop being
   * covered. These are removed in a hook instead, leaving the defaults in force.
   */
  FORBID_CONTENT_TAGS?: string[]
}

// Saneringen kjører med ALLOW_DATA_ATTR: false, så data-attributter må stå
// eksplisitt i ALLOWED_ATTR. Ellers ville brødtekst kunne bære data-citation-*
// og forfalske kildeangivelsen i «kopier med kilde».
export const htmlSanitizationPolicies: Record<HtmlSanitizationPolicy, HtmlSanitizationConfig> = {
  highlight: {
    ALLOWED_TAGS: ['em', 'span'],
    ALLOWED_ATTR: ['class', 'id'],
  },
  richText: {
    // nav og section er strukturelle wrappere fra EPUB-nav og JATS. Uten dem blir
    // <nav epub:type="toc"> og <nav epub:type="page-list"> til to nakne <ol> på
    // toppnivå, og innholdsfortegnelsen viser sidelisten som kapitler.
    // Legal commentary footnotes are authored as sanitized rich text with
    // inline toggle buttons and hidden note bodies. Keep that contract so
    // `useFootnotes` can open/close notes on click.
    // `data-id` bærer Solr-node-id-en på lovhenvisningene i fagartikler.
    // Uten den finner ikke `useArticleInteractions` hvilken lov som skal
    // åpnes, og Kilder-panelet kan ikke ta imot nye henvisninger.
    ALLOWED_TAGS: ['a', 'b', 'blockquote', 'br', 'button', 'caption', 'col', 'colgroup', 'dd', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'i', 'img', 'li', 'nav', 'ol', 'p', 'section', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul'],
    ALLOWED_ATTR: ['alt', 'aria-controls', 'aria-expanded', 'aria-label', 'class', 'colspan', 'data-fn-id', 'data-footnote-id', 'data-id', 'data-listitem-continue', 'data-normalized', 'data-parentes', 'epub:type', 'headers', 'hidden', 'href', 'id', 'rel', 'rowspan', 'scope', 'src', 'start', 'style', 'target', 'title', 'type'],
    ALLOWED_STYLES: {
      ol: {
        'counter-reset': [/^list-counter \d+$/],
        'list-style-type': [/^(?:decimal-leading-zero|lower-latin|lower-roman|upper-roman)$/],
      },
      ul: {
        'list-style-type': [/^circle$/],
      },
    },
  },
  // Tidsskriftsartikler lagres som JATS-XML i Solr og normaliseres til HTML på
  // klienten (normalizeJats). Saneres de med richText FØR normaliseringen, fjernes
  // xref/fn/fig/graphic med teksten beholdt: notenummeret står igjen som ren
  // tekst uten mål, og figurene forsvinner uten plassholder. Denne policyen
  // beholder elementene og attributtene normalizeJats trenger, og fjerner
  // fortsatt alt kjørbart.
  jats: {
    ALLOWED_TAGS: [
      'a', 'b', 'blockquote', 'br', 'caption', 'col', 'colgroup', 'dd', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'i', 'img', 'li', 'nav', 'ol', 'p', 'section', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul',
      'alt-text', 'bold', 'ext-link', 'fig', 'fn', 'graphic', 'italic', 'label', 'list', 'list-item', 'sec', 'title', 'xref',
    ],
    ALLOWED_ATTR: [
      'alt', 'aria-label', 'class', 'colspan', 'data-listitem-continue', 'data-normalized', 'data-parentes', 'epub:type', 'headers', 'href', 'id', 'rel', 'rowspan', 'scope', 'src', 'start', 'style', 'target', 'title', 'type',
      'ext-link-type', 'list-type', 'ref-type', 'rid', 'xlink:href',
    ],
  },
  marking: {
    ALLOWED_TAGS: [...MARKING_STORED_TAGS],
    ALLOWED_ATTR: ['class', 'src', 'alt'],
    ALLOWED_URI_REGEXP: /^(?:https?:\/\/|\/(?!\/))/i,
    // textarea/option are raw-text elements whose content survives an
    // innerHTML round-trip and can re-parse as markup.
    FORBID_CONTENT_TAGS: [...MARKING_REMOVED_TAGS, 'textarea', 'option'],
  },
  rss: {
    ALLOWED_TAGS: ['a', 'b', 'blockquote', 'br', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'li', 'ol', 'p', 'span', 'strong', 'u', 'ul'],
    ALLOWED_ATTR: ['href', 'rel', 'target'],
  },
}